/**
 * 업무 프로젝트 노트 패널(J5b1) — 프로젝트 화면 오른쪽 서랍에서 노트 목록·만들기·읽기·고치기·복제·삭제.
 *
 * 주소가 곧 열림 상태다(`?notes=1` = 목록 · `?note={id}` = 그 노트 — 부모가 key 로 다시 그린다).
 * 본문은 마크다운 평문(body_md)이고 읽기는 Markdown 컴포넌트로만 그린다.
 * 🔴 서버는 버전부터 본다 — 한 번에 PATCH 하나만 보내고 늘 최신 note.version 을 싣는다
 *    (제목 blur 와 본문 저장이 겹쳐 스스로 409 를 만들지 않게).
 * 🔴 Esc 는 패널 루트 onKeyDown 에서만 받는다(window 리스너로 받으면 확인 대화상자의 Esc 가
 *    전파돼 패널까지 닫힌다 — 업무 상세 서랍과 같은 규칙).
 * 댓글·버전 기록은 J5b2, 전역 «노트» 페이지는 J5b3 이다.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { confirmDialog, toast } from '../../components/AppDialog';
import { copyToClipboard } from '../../utils/clipboard';
import Markdown from '../../components/Markdown';
import { kstDateKey, fmtMsgTime } from '../../utils/msgDate';
import { COLORS, RADIUS, btn, card, input } from '../../utils/uiStyles';

// 🔴 '없는 노트' 와 '못 보는 노트' 는 같은 문구다(서버도 그렇다 — 존재를 알려주지 않는다).
const NOT_FOUND = '노트를 찾을 수 없거나 볼 권한이 없습니다.';
// 🔴 서버(task_pm_notes.py STALE)와 글자 그대로 같아야 한다 — 이 문구일 때만 편집 글을 지키고 다시 불러오기를 준다.
const STALE = '다른 사람이 먼저 노트를 고쳤습니다. 새로 불러온 뒤 다시 저장해 주세요.';
const DELETE_CONFIRM = '이 노트를 지울까요? 댓글·기록도 함께 지워지고 되돌릴 수 없습니다.';
const LEAVE_CONFIRM = '저장하지 않은 내용이 사라집니다. 나갈까요?';
const SAVE_FAIL = '저장하지 못했습니다.';
const SEARCH_DEBOUNCE_MS = 300;

// 정렬은 화면에서 한다 — 서버 목록은 늘 '만든 최신 순(id 내림)' 하나뿐이다.
const SORTS = [
  { value: 'created', label: '최근 만든 순' },
  { value: 'updated', label: '최근 수정 순' },
  { value: 'title', label: '제목 순' },
];

// 카드 날짜는 날짜만(KST), 상세 메타 시각은 fmtMsgTime 그대로 쓴다(새 형식 함수를 만들지 않는다).
const cardDay = (iso) => kstDateKey(iso).replaceAll('-', '.');
const statusOf = (e) => e?.response?.status;

const headerRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' };
const alertBox = { ...card, padding: '10px 12px', background: COLORS.dangerSoft, color: COLORS.dangerText, fontSize: '12px', marginBottom: '10px' };

export default function TaskPmNotesPanel({ project, noteId, canWrite, onOpenNote, onBackToList, onClose }) {
  // ── 목록
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [listError, setListError] = useState('');
  const [search, setSearch] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [sortBy, setSortBy] = useState('created');

  // ── 새 노트
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [newError, setNewError] = useState('');
  const [newBusy, setNewBusy] = useState(false);

  // ── 상세
  const [note, setNote] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [bodyEdit, setBodyEdit] = useState(null);     // null = 읽기, 문자열 = 편집 중
  const [saveError, setSaveError] = useState('');
  const [stale, setStale] = useState(false);          // 409 STALE 일 때만 «다시 불러오기»
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const rootRef = useRef(null);
  const menuWrapRef = useRef(null);
  const listSeqRef = useRef(0);
  const detailSeqRef = useRef(0);
  // 🔴 PATCH 는 한 번에 하나만 — 제목 blur 와 본문 저장이 겹치면 둘째 요청이 낡은 base_version 을 싣는다.
  const savingRef = useRef(false);
  const sentTitleRef = useRef(null);                  // 마지막으로 보낸 제목(같으면 다시 안 보낸다)
  // (#1303) 패널이 사라진 뒤 온 만들기·복제·삭제 응답이 주소를 바꾸지 않게(닫은 패널·열린 업무를 덮는다).
  const aliveRef = useRef(true);

  const showDetail = noteId !== null && noteId !== undefined && String(noteId) !== '';

  // 패널이 열리면 루트에 포커스를 둔다(Esc 를 바로 받게).
  useEffect(() => { rootRef.current?.focus(); }, []);

  // 🔴 마운트할 때 true 를 다시 쓴다 — 지금은 StrictMode 가 없지만, 켜면 개발 모드가 effect 를 정리→재실행하므로
  //    정리에서 false 로만 두면 다시 마운트된 패널이 영영 «죽은» 것으로 보인다(켜도 안전하게).
  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);

  // 검색은 타이핑이 멈춘 뒤에 보낸다.
  useEffect(() => {
    if (search.trim() === appliedQ.trim()) return undefined;
    const timer = setTimeout(() => setAppliedQ(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, appliedQ]);

  // ── 목록 불러오기 — 🔴 목록을 보고 있을 때만 묻는다(상세·새 노트에서는 보내지 않는다).
  useEffect(() => {
    if (showDetail || creating) return;
    const seq = listSeqRef.current + 1;
    listSeqRef.current = seq;
    setListError('');
    taskPmAPI.listProjectNotes(project.id, appliedQ ? { q: appliedQ } : {})
      .then((res) => {
        if (listSeqRef.current !== seq) return;                    // 늦게 온 응답은 버린다
        setItems(Array.isArray(res?.data?.items) ? res.data.items : []);
        setTotal(Number(res?.data?.total || 0));
      })
      .catch((e) => {
        if (listSeqRef.current !== seq) return;
        setItems([]);
        setTotal(0);
        setListError(errorText(e, '노트 목록을 불러오지 못했습니다.'));
      });
  }, [project.id, appliedQ, showDetail, creating]);

  // ── 상세 불러오기
  useEffect(() => {
    if (!showDetail) return;
    const seq = detailSeqRef.current + 1;
    detailSeqRef.current = seq;
    taskPmAPI.getNote(noteId)
      .then((res) => {
        if (detailSeqRef.current !== seq) return;
        const data = res?.data || null;
        // 🔴 다른 프로젝트의 노트는 못 본 것으로 한다 — 권한·키가 이 프로젝트 기준이라 어긋난다.
        if (!data || String(data.project_id) !== String(project.id)) {
          setNote(null);
          setDetailError(NOT_FOUND);
          return;
        }
        setNote(data);
        setTitleDraft(data.title || '');
        sentTitleRef.current = data.title || '';
        setDetailError('');
      })
      .catch((e) => {
        if (detailSeqRef.current !== seq) return;
        const code = statusOf(e);
        setNote(null);
        setDetailError(code === 404 || code === 403 ? NOT_FOUND : errorText(e, '노트를 불러오지 못했습니다.'));
      });
  }, [noteId, showDetail, project.id]);

  // 더보기 메뉴 바깥을 누르면 닫는다(Esc 는 루트 onKeyDown 이 맡는다).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => {
      if (menuWrapRef.current && menuWrapRef.current.contains(e.target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  // ── 화면에서 정렬한다(서버는 만든 최신 순 하나뿐이다).
  const rows = useMemo(() => {
    const list = [...items];
    if (sortBy === 'updated') {
      list.sort((a, b) => {
        const av = a.updated_at || '';
        const bv = b.updated_at || '';
        if (av === bv) return Number(b.id || 0) - Number(a.id || 0);
        return av < bv ? 1 : -1;
      });
    } else if (sortBy === 'title') {
      list.sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'ko'));
    }
    return list;
  }, [items, sortBy]);

  const newDirty = Boolean(newTitle || newBody);
  const bodyDirty = bodyEdit !== null && bodyEdit !== (note?.body_md || '');

  // 편집 글이 바뀐 채로 떠나려 하면 한 번 묻는다(Esc 로 편집만 닫는 길은 묻지 않는다).
  const leave = async (dirty, go) => {
    if (dirty && !(await confirmDialog(LEAVE_CONFIRM))) return;
    go();
  };

  const backToList = () => {
    setCreating(false);
    setNewTitle('');
    setNewBody('');
    setNewError('');
    onBackToList();
  };

  // ── 저장(PATCH) — 🔴 늘 최신 note.version 을 싣고, 진행 중이면 보내지 않는다.
  const save = (fields) => {
    if (!note || savingRef.current) return;
    savingRef.current = true;
    setBusy(true);
    if (typeof fields.title === 'string') sentTitleRef.current = fields.title;
    taskPmAPI.updateNote(note.id, { base_version: note.version, ...fields })
      .then((res) => {
        const data = res?.data;
        if (data) {
          setNote(data);
          setTitleDraft(data.title || '');
          sentTitleRef.current = data.title || '';
        }
        setBodyEdit(null);
        setSaveError('');
        setStale(false);
      })
      .catch((e) => {
        const detail = errorText(e, SAVE_FAIL);
        // 🔴 먼저 고쳐진 경우에만 편집 글을 그대로 두고 «다시 불러오기» 를 준다.
        const isStale = statusOf(e) === 409 && detail === STALE;
        setSaveError(detail);
        setStale(isStale);
      })
      .finally(() => {
        savingRef.current = false;
        setBusy(false);
      });
  };

  // 제목은 blur 한 곳에서만 보낸다(Enter 는 blur 를 부른다).
  const handleTitleBlur = () => {
    if (!note || !note.can_edit) return;
    const value = titleDraft.trim();
    if (!value) { setTitleDraft(note.title || ''); return; }        // 빈 값은 서버 값으로 되돌린다
    if (value === (note.title || '')) return;                       // 서버 값과 같다
    if (value === sentTitleRef.current) return;                     // 마지막으로 보낸 값과 같다
    if (bodyEdit !== null) return;                                  // 본문 «저장» 이 같이 싣는다
    save({ title: value });
  };

  const saveBody = () => {
    if (!note || bodyEdit === null) return;
    const fields = { body_md: bodyEdit };
    const value = titleDraft.trim();
    if (note.can_edit && value && value !== (note.title || '')) fields.title = value;
    save(fields);
  };

  const reloadNote = () => {
    taskPmAPI.getNote(note ? note.id : noteId)
      .then((res) => {
        const data = res?.data;
        if (!data) return;
        setNote(data);                       // 🔴 편집 중인 글(bodyEdit)은 그대로 둔다
        setSaveError('');
        setStale(false);
      })
      .catch((e) => setSaveError(errorText(e, '노트를 불러오지 못했습니다.')));
  };

  const createNote = async () => {
    const title = newTitle.trim();
    if (!title || newBusy) return;
    setNewBusy(true);
    setNewError('');
    try {
      const res = await taskPmAPI.createNote(project.id, { title, body_md: newBody });
      toast('노트를 만들었습니다.');
      // 패널이 이미 닫혔으면(닫기·업무 열기·화면 이동) 주소를 바꾸지 않는다 — 다른 화면을 덮는다.
      if (aliveRef.current) onOpenNote(res?.data?.id);
    } catch (e) {
      setNewError(errorText(e, '노트를 만들지 못했습니다.'));
    } finally {
      setNewBusy(false);
    }
  };

  const duplicateNote = async () => {
    setMenuOpen(false);
    if (!note) return;
    try {
      const res = await taskPmAPI.duplicateNote(note.id);
      toast('노트를 복제했습니다.');
      if (aliveRef.current) onOpenNote(res?.data?.id);
    } catch (e) {
      setSaveError(errorText(e, '노트를 복제하지 못했습니다.'));
    }
  };

  const removeNote = async () => {
    setMenuOpen(false);
    if (!note) return;
    if (!(await confirmDialog(DELETE_CONFIRM))) return;
    try {
      await taskPmAPI.deleteNote(note.id);
      toast('노트를 지웠습니다.');
      if (aliveRef.current) onBackToList();
    } catch (e) {
      setSaveError(errorText(e, '노트를 지우지 못했습니다.'));
    }
  };

  // 키 배지 = 이 노트로 바로 오는 주소(권한이 없는 환경도 있어 성공·실패 둘 다 알린다).
  const copyLink = () => {
    if (!note) return;
    Promise.resolve(copyToClipboard(`${window.location.origin}/tasks/${project.id}?note=${note.id}`)).then(
      () => toast('링크를 복사했습니다.'),
      () => toast('링크를 복사하지 못했습니다.', 'error'),
    );
  };

  // 🔴 Esc 는 여기 한 곳에서만 받는다. 메뉴가 열려 있으면 메뉴만 닫는다 — «더보기» 를 누른 뒤
  //    포커스가 그 버튼에 남아 키 이벤트가 메뉴를 거치지 않고 루트로 바로 올라온다.
  const handleKeyDown = (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (menuOpen) { setMenuOpen(false); return; }
    if (bodyEdit !== null) { setBodyEdit(null); return; }
    // 만드는 중에는 새 노트 화면도 패널도 닫지 않는다(응답이 노트를 열거나 입력을 돌려준다).
    if (creating) { if (!newBusy) setCreating(false); return; }
    onClose();
  };

  // 업무 상세 서랍(TaskPmTaskDetail)과 같은 치수·모양이다.
  const shell = (children) => (
    <div ref={rootRef} tabIndex={-1} role="dialog" aria-label="노트" onKeyDown={handleKeyDown}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)', zIndex: 60,
        background: COLORS.surface, borderLeft: `1px solid ${COLORS.border}`,
        boxShadow: '-2px 0 12px rgba(15,23,42,.12)', overflowY: 'auto', padding: '14px 20px 20px', outline: 'none',
      }}>
      {children}
    </div>
  );

  // locked = 요청 중이라 떠날 수 없다(새 노트 만들기 중에만 넘긴다).
  const backButton = (dirty, locked = false) => (
    <button type="button" aria-label="목록으로" onClick={() => leave(dirty, backToList)} disabled={locked}
      style={{ ...btn('secondary', 'sm'), width: '28px', height: '28px', boxSizing: 'border-box', padding: 0 }}>‹</button>
  );
  const closeButton = (dirty, auto = false, locked = false) => (
    <button type="button" onClick={() => leave(dirty, onClose)} disabled={locked}
      style={{ ...btn('secondary', 'sm'), ...(auto ? { marginLeft: 'auto' } : null) }}>닫기</button>
  );

  // ───────────────────────────────────────────── 새 노트
  if (creating) {
    return shell(
      <>
        <div style={headerRow}>
          {backButton(newDirty, newBusy)}
          <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>새 노트</h2>
          {closeButton(newDirty, true, newBusy)}
        </div>
        {newError && <div role="alert" style={alertBox}>{newError}</div>}
        {/* 🔴 만드는 중에는 제목·본문이 읽기 전용이고 떠나는 단추가 잠긴다 — 잠그지 않으면 그사이 친 글자는
            저장되지 않고 사라지고, 떠난 뒤 온 응답이 노트를 연다. disabled 는 쓰지 않는다(초점을 빼서 화면이 흔들린다). */}
        <input aria-label="노트 제목" maxLength={300} placeholder="제목을 입력하세요" value={newTitle}
          readOnly={newBusy} aria-busy={newBusy}
          onChange={(e) => setNewTitle(e.target.value)}
          style={{ ...input, height: '40px', fontSize: '18px', marginBottom: '8px' }} />
        <textarea aria-label="노트 본문" placeholder="마크다운으로 적을 수 있습니다" value={newBody}
          readOnly={newBusy} aria-busy={newBusy}
          onChange={(e) => setNewBody(e.target.value)}
          style={{ ...input, height: '320px', resize: 'vertical', fontSize: '13px', lineHeight: 1.6 }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
          <button type="button" onClick={() => leave(newDirty, backToList)} disabled={newBusy} style={btn('secondary', 'sm')}>취소</button>
          <button type="button" onClick={createNote} disabled={!newTitle.trim() || newBusy}
            style={{ ...btn('primary', 'sm'), opacity: !newTitle.trim() || newBusy ? 0.5 : 1 }}>만들기</button>
        </div>
      </>
    );
  }

  // ───────────────────────────────────────────── 상세
  if (showDetail) {
    const menuItems = [];
    if (note?.can_edit) menuItems.push({ key: 'edit', label: '수정', run: () => { setMenuOpen(false); setBodyEdit(note.body_md || ''); } });
    if (note && canWrite) menuItems.push({ key: 'duplicate', label: '노트 복제', run: duplicateNote });
    if (note?.can_delete) menuItems.push({ key: 'delete', label: '삭제', danger: true, run: removeNote });

    return shell(
      <>
        <div style={{ ...headerRow, minHeight: '36px', gap: '6px', position: 'relative' }}>
          {backButton(bodyDirty)}
          {note && (
            <button type="button" aria-label="링크 복사" title="링크 복사" onClick={copyLink}
              style={{ ...btn('secondary', 'sm'), height: '24px', boxSizing: 'border-box', padding: '0 8px', fontSize: '11px', color: COLORS.textSub }}>{note.key}</button>
          )}
          <span style={{ marginLeft: 'auto' }} />
          {menuItems.length > 0 && (
            <div ref={menuWrapRef} style={{ position: 'relative' }}>
              <button type="button" aria-label="더보기" aria-haspopup="menu" aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
                style={{ ...btn('secondary', 'sm'), width: '28px', height: '28px', boxSizing: 'border-box', padding: 0 }}>⋯</button>
              {menuOpen && (
                <div role="menu" style={{ position: 'absolute', top: '32px', right: 0, zIndex: 1, width: '180px', ...card, padding: '4px' }}>
                  {menuItems.map((m) => (
                    <button key={m.key} type="button" role="menuitem" onClick={m.run}
                      style={{
                        display: 'flex', alignItems: 'center', width: '100%', height: '32px', padding: '0 10px',
                        border: 'none', background: 'none', cursor: 'pointer', borderRadius: RADIUS.sm,
                        fontSize: '13px', textAlign: 'left', color: m.danger ? COLORS.dangerText : COLORS.text,
                      }}>{m.label}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {closeButton(bodyDirty)}
        </div>

        {detailError && <div role="alert" style={alertBox}>{detailError}</div>}
        {!detailError && !note && <p role="status" style={{ fontSize: '12px', color: COLORS.textSub }}>불러오는 중…</p>}

        {note && (
          <>
            {/* 🔴 제목·본문은 저장 중 읽기 전용 — 그사이 친 글자를 늦게 온 응답이 덮지 않게.
                disabled 는 쓰지 않는다(초점을 빼서 제목 blur 저장을 다시 부른다). */}
            {note.can_edit ? (
              <input aria-label="노트 제목" maxLength={300} value={titleDraft}
                readOnly={busy} aria-busy={busy}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={(e) => {
                  if (e.nativeEvent?.isComposing) return;                 // 한글 조합 중에는 둘 다 무시
                  if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); return; }
                  if (e.key === 'Escape') { setTitleDraft(note.title || ''); e.preventDefault(); }
                }}
                style={{ ...input, height: '40px', fontSize: '20px', fontWeight: 700, border: '1px solid transparent' }} />
            ) : (
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>{note.title}</h3>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: COLORS.textSub, padding: '8px 0', borderBottom: `1px solid ${COLORS.borderSoft}`, marginBottom: '12px' }}>
              <span>{`${project.key || ''} · 최종 수정 ${fmtMsgTime(note.updated_at)}`}</span>
              <span style={{ marginLeft: 'auto' }}>{note.updated_by_name || note.created_by_name || ''}</span>
            </div>

            {saveError && (
              <div role="alert" style={alertBox}>
                {saveError}
                {stale && (
                  <button type="button" onClick={reloadNote}
                    style={{ ...btn('secondary', 'sm'), marginLeft: '8px' }}>다시 불러오기</button>
                )}
              </div>
            )}

            {bodyEdit === null ? (
              (note.body_md || '').trim()
                ? <Markdown>{note.body_md}</Markdown>
                : <p style={{ margin: 0, fontSize: '13px', color: COLORS.textMuted }}>본문이 없습니다.</p>
            ) : (
              <>
                <textarea aria-label="노트 본문" value={bodyEdit} onChange={(e) => setBodyEdit(e.target.value)}
                  readOnly={busy} aria-busy={busy}
                  style={{ ...input, height: '360px', resize: 'vertical', fontSize: '13px', lineHeight: 1.6 }} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '8px' }}>
                  <button type="button" onClick={() => { setBodyEdit(null); setSaveError(''); setStale(false); }}
                    style={btn('secondary', 'sm')}>취소</button>
                  <button type="button" onClick={saveBody} disabled={busy}
                    style={{ ...btn('primary', 'sm'), opacity: busy ? 0.5 : 1 }}>저장</button>
                </div>
              </>
            )}
          </>
        )}
      </>
    );
  }

  // ───────────────────────────────────────────── 목록
  return shell(
    <>
      <div style={headerRow}>
        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>노트</h2>
        {canWrite && (
          <button type="button" onClick={() => { setNewTitle(''); setNewBody(''); setNewError(''); setCreating(true); }}
            style={{ ...btn('primary', 'sm'), minWidth: '75px', height: '30px', boxSizing: 'border-box', marginLeft: 'auto' }}>새 노트</button>
        )}
        <button type="button" onClick={onClose}
          style={{ ...btn('secondary', 'sm'), ...(canWrite ? null : { marginLeft: 'auto' }) }}>닫기</button>
      </div>

      <input type="search" aria-label="노트 검색" placeholder="제목·본문 검색" value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ ...input, height: '35px' }} />
      <select aria-label="노트 정렬" value={sortBy} onChange={(e) => setSortBy(e.target.value)}
        style={{ ...input, height: '35px', marginTop: '6px', marginBottom: '12px' }}>
        {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
      </select>

      {listError && <div role="alert" style={alertBox}>{listError}</div>}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {rows.map((n) => (
          <li key={n.id}>
            <button type="button" aria-label={`${n.key} ${n.title}`} onClick={() => onOpenNote(n.id)}
              style={{ ...card, width: '100%', boxSizing: 'border-box', padding: '12px', marginBottom: '8px', textAlign: 'left', cursor: 'pointer' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</div>
              <div style={{ marginTop: '4px', fontSize: '12px', color: COLORS.textSub }}>
                {`${n.key} · ${cardDay(n.updated_at)} · ${n.updated_by_name || n.created_by_name || ''}`}
              </div>
            </button>
          </li>
        ))}
      </ul>

      {!listError && rows.length === 0 && (
        <p style={{ margin: 0, fontSize: '13px', color: COLORS.textSub }}>
          {appliedQ ? '검색 결과가 없습니다.' : '노트가 없습니다.'}
        </p>
      )}
      {total > rows.length && (
        <p style={{ margin: '8px 0 0', fontSize: '12px', color: COLORS.textSub }}>처음 {rows.length}개만 표시합니다(전체 {total}개).</p>
      )}
    </>
  );
}
